import { apiClient } from '@/api/client';
import { MAX_PHOTOS_PER_CHECK_IN } from '@/constants/photos';
import { photoUploadFields, type PhotoUploadOptions } from '@/utils/photo-validation';
import type { CheckIn } from '@/types/check-in';
import type { Paginated } from '@/types/pagination';
import type { Photo } from '@/types/photo';
import type { Review } from '@/types/review';
import {
  mapCheckInWireToDomain,
  mapPaginatedCheckInsWireToDomain,
} from '../mappers/check-in';
import { mapPhotoWireToDomain } from '../mappers/photo';
import { mapReviewWireToDomain } from '../mappers/review';
import {
  CheckInWire,
  checkInWireSchema,
  CheckInCreate,
  CheckInUpdate,
  PaginatedCheckInResponseWire,
  paginatedCheckInResponseSchema
} from '../schemas/check-in';
import { PhotoWire, photoWireSchema } from '../schemas/photo';
import { ReviewWire, reviewWireSchema, ReviewCreate } from '../schemas/review';

/**
 * Re-export request DTO types so consumers depend on the endpoint seam
 * rather than importing wire schemas directly.
 */
export type { CheckInCreate, CheckInUpdate } from '../schemas/check-in';
export type { ReviewCreate } from '../schemas/review';

/**
 * Domain-oriented API module for check-in operations
 * Provides typed functions for check-in CRUD from the Gateway API
 */

/**
 * Query parameters for listing check-ins
 */
export interface ListCheckInsParams {
  menu_item_id?: string;
  business_id?: string;
  status?: 'draft' | 'active' | 'deleted';
  page?: number;
  per_page?: number;
  sort_by?: 'created_at' | 'occurred_at';
  sort_order?: 'asc' | 'desc';
}

/**
 * Build query string from parameters
 */
function buildQueryString(params: ListCheckInsParams): string {
  const queryParams: string[] = [];

  if (params.menu_item_id) {
    queryParams.push(`menu_item_id=${encodeURIComponent(params.menu_item_id)}`);
  }
  if (params.business_id) {
    queryParams.push(`business_id=${encodeURIComponent(params.business_id)}`);
  }
  if (params.status) {
    queryParams.push(`status=${params.status}`);
  }
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }
  if (params.sort_by) {
    queryParams.push(`sort_by=${params.sort_by}`);
  }
  if (params.sort_order) {
    queryParams.push(`sort_order=${params.sort_order}`);
  }

  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
}

/**
 * Create a new check-in (creates DRAFT status)
 *
 * @param data - Check-in creation data
 * @returns Created check-in
 *
 * @example
 * ```typescript
 * const checkIn = await createCheckIn({
 *   business_id: 'business-123',
 *   menu_item_id: 'menu-item-456',
 *   rating_id: 1,
 *   latitude: 39.1653,
 *   longitude: -86.5264
 * });
 * ```
 */
export async function createCheckIn(data: CheckInCreate): Promise<CheckIn> {
  const endpoint = '/api/check-ins';
  const wireData = await apiClient.post<CheckInWire>(endpoint, data);

  // Validate wire response, then map to the domain type
  return mapCheckInWireToDomain(checkInWireSchema.parse(wireData));
}

/**
 * Update an existing check-in (only allowed for DRAFT status)
 *
 * @param id - Check-in ID
 * @param data - Check-in update data
 * @returns Updated check-in
 *
 * @example
 * ```typescript
 * const checkIn = await updateCheckIn('check-in-123', {
 *   rating_id: 2
 * });
 * ```
 */
export async function updateCheckIn(
  id: string,
  data: CheckInUpdate
): Promise<CheckIn> {
  const endpoint = `/api/check-ins/${id}`;
  const wireData = await apiClient.put<CheckInWire>(endpoint, data);

  // Validate wire response, then map to the domain type
  return mapCheckInWireToDomain(checkInWireSchema.parse(wireData));
}

/**
 * Finalize a check-in (changes status from DRAFT to ACTIVE)
 *
 * @param id - Check-in ID
 * @returns Finalized check-in
 *
 * @example
 * ```typescript
 * const checkIn = await finalizeCheckIn('check-in-123');
 * ```
 */
export async function finalizeCheckIn(id: string): Promise<CheckIn> {
  const endpoint = `/api/check-ins/${id}/finalize`;
  const wireData = await apiClient.post<CheckInWire>(endpoint);

  // Validate wire response, then map to the domain type
  return mapCheckInWireToDomain(checkInWireSchema.parse(wireData));
}

export interface CheckInPhotoUploadOptions extends PhotoUploadOptions {
  /**
   * The photo's place in the user's selection (0-based). Sent as the multipart
   * `position` field so the gateway keeps the chosen order (and cover photo)
   * even though uploads run in parallel. Omitted unless an integer 0..2.
   */
  position?: number;
}

function isValidPhotoPosition(position: number | undefined): position is number {
  return (
    position !== undefined &&
    Number.isInteger(position) &&
    position >= 0 &&
    position < MAX_PHOTOS_PER_CHECK_IN
  );
}

/** Multipart fields for a check-in photo: alt text plus the selection position. */
function checkInPhotoUploadFields({
  position,
  ...options
}: CheckInPhotoUploadOptions): Record<string, string> | undefined {
  const fields = photoUploadFields(options);
  if (!isValidPhotoPosition(position)) return fields;
  return { ...fields, position: String(position) };
}

/**
 * Upload a photo for a check-in
 *
 * @param checkInId - Check-in ID
 * @param file - File to upload
 * @param options - Optional alt text and selection position
 * @returns Uploaded photo metadata
 *
 * @example
 * ```typescript
 * const photo = await uploadCheckInPhoto('check-in-123', {
 *   uri: 'file:///path/to/photo.jpg',
 *   type: 'image/jpeg',
 *   name: 'photo.jpg'
 * });
 * ```
 */
export async function uploadCheckInPhoto(
  checkInId: string,
  file: { uri: string; type?: string; name?: string; file?: File },
  options: CheckInPhotoUploadOptions = {}
): Promise<Photo> {
  const endpoint = `/api/check-ins/${checkInId}/photos`;
  const wireData = await apiClient.uploadFile<PhotoWire>(
    endpoint,
    file,
    'photo',
    checkInPhotoUploadFields(options)
  );

  // Validate wire response, then map to the domain type
  return mapPhotoWireToDomain(photoWireSchema.parse(wireData));
}

/**
 * Delete a check-in photo
 *
 * @param photoId - Photo ID
 *
 * @example
 * ```typescript
 * await deleteCheckInPhoto('photo-123');
 * ```
 */
export async function deleteCheckInPhoto(photoId: string): Promise<void> {
  const endpoint = `/api/photos/${photoId}`;
  await apiClient.delete(endpoint);
}

/**
 * Create a review for a check-in
 *
 * @param checkInId - Check-in ID
 * @param data - Review content
 * @returns Created review
 *
 * @example
 * ```typescript
 * const review = await createReview('check-in-123', {
 *   content: 'Amazing dish! The flavors were incredible.'
 * });
 * ```
 */
export async function createReview(
  checkInId: string,
  data: ReviewCreate
): Promise<Review> {
  const endpoint = `/api/check-ins/${checkInId}/review`;
  const wireData = await apiClient.post<ReviewWire>(endpoint, data);

  // Validate wire response, then map to the domain type
  return mapReviewWireToDomain(reviewWireSchema.parse(wireData));
}

/**
 * Create a review reminder for a check-in
 *
 * @param checkInId - Check-in ID
 * @returns Success response
 *
 * @example
 * ```typescript
 * await createReminder('check-in-123');
 * ```
 */
export async function createReminder(checkInId: string): Promise<void> {
  const endpoint = `/api/check-ins/${checkInId}/remind`;
  await apiClient.post(endpoint);
}

/**
 * Get the current user's check-ins
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of check-ins
 *
 * @example
 * ```typescript
 * const result = await getMyCheckIns({
 *   status: 'active',
 *   page: 1,
 *   per_page: 20
 * });
 * ```
 */
export async function getMyCheckIns(
  params: ListCheckInsParams = {}
): Promise<Paginated<CheckIn>> {
  const queryParams: ListCheckInsParams = {
    page: 1,
    per_page: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
    ...params,
  };

  const queryString = buildQueryString(queryParams);
  const endpoint = `/api/users/me/check-ins${queryString}`;

  const wireData = await apiClient.get<PaginatedCheckInResponseWire>(endpoint);

  // Validate wire response, then map to the domain Paginated envelope
  return mapPaginatedCheckInsWireToDomain(paginatedCheckInResponseSchema.parse(wireData));
}

/**
 * Get a single check-in by ID
 *
 * @param id - Check-in ID
 * @returns Check-in
 *
 * @example
 * ```typescript
 * const checkIn = await getCheckInById('check-in-123');
 * ```
 */
export async function getCheckInById(id: string): Promise<CheckIn> {
  const endpoint = `/api/check-ins/${id}`;
  const wireData = await apiClient.get<CheckInWire>(endpoint);

  // Validate wire response, then map to the domain type
  return mapCheckInWireToDomain(checkInWireSchema.parse(wireData));
}

/**
 * Restore a soft-deleted check-in (sets status back to ACTIVE)
 *
 * @param id - Check-in ID
 * @returns Restored check-in
 */
export async function restoreCheckIn(id: string): Promise<CheckIn> {
  const endpoint = `/api/check-ins/${id}/restore`;
  const wireData = await apiClient.post<CheckInWire>(endpoint);

  // Validate wire response, then map to the domain type
  return mapCheckInWireToDomain(checkInWireSchema.parse(wireData));
}

/**
 * Delete a check-in (soft delete - sets status to DELETED)
 *
 * @param id - Check-in ID
 *
 * @example
 * ```typescript
 * await deleteCheckIn('check-in-123');
 * ```
 */
export async function deleteCheckIn(id: string): Promise<void> {
  const endpoint = `/api/check-ins/${id}`;
  await apiClient.delete(endpoint);
}
